/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useState, useRef, useContext, useEffect } from 'react';
import { Col, Row } from 'components/Grid';
import { GridColumnTitle, SelectField } from 'components/Form';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';
import { Field, Formik } from 'formik';
import { FitinsurAPI, MessageCenter } from "utils";
import * as yup from "yup";
import Modal from 'components/ModalContacts';
import ModalConfirmation from 'components/ModalConfirmation';
import Loader from 'components/Loader';
import Colors from 'constants/Colors';
import Icon from 'components/Icon';

import {
  TableHeader,
  TableCollum,
  CustomField,
  InlineLabel,
  TableHead,
  TableRow,
  TableBody,
  Table,
} from '../styles';

import { Scrollbox, LabelEdit, IconSeparator, InfoReport } from './styles';

export default function ModalContacts({ onClose, selectedPayments, showModal, onUpdated, props, isChangingAborted, statusOptions, handleResettingPaymentSelection }) {
  const formik = useRef(null);
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [validations, setValidations] = useState({});
  const [initial, setInitial] = useState({ status: '' });
  const [modalConfirmation, setModalConfirmation] = useState(false);
  const [modalConfirmationRisk, setModalConfirmationRisk] = useState(false);
  const [modalReportUpdate, setModalReportUpdate] = useState(false);
  const [updatedPayments, setUpdatedPayments] = useState([]);

  const createValidation = () => {
    let validations = yup.object().shape({
      status: yup.string().required("* Required field")
    });
    setValidations(validations);
    return validations;
  };

  const handleUpdate = (selectedStatus) => {
    loading.start();
    selectedPayments.map(payment => {
      if (payment.status.label === 'Aborted' && !props.permissions.PAYMENTS_CHANGE_ABORTED) {
        return mc.error("This user is not allowed to change an aborted payment.");
      }
    })
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestUpdateInstallments(selectedPayments, selectedStatus).then(
        response => response.json()).then(data => {
          if (data.ret_code !== 200) {
            mc.error(data.ret_msg_details);
            formik.current.values.status = '';
            loading.stop();
            return;
          }

          if (selectedStatus === 'aborted') {
            handleResettingPaymentSelection();
          }

          formik.current.values.status = '';
          setUpdatedPayments(data.ret_value)
          setModalReportUpdate(true);
          loading.stop();
        }
        );
    } catch (error) {
      loading.stop();
      mc.error("An error happened, please try again.");
    }
  }

  const handleConfirmationUpdate = (values) => {
    createValidation().validate(values).catch((e) => {
      console.log("validation error", e);
      mc.error("Please fill in all required fields.");
    });

    if (values.status) {
      setModalConfirmation(true)
      onClose()
    }
  }

  useEffect(() => {
    createValidation();
  }, []);

  return (
    <>
      <Formik
        innerRef={formik}
        initialValues={initial}
        enableReinitialize={true}
        validationSchema={validations}
      >
        {({ values, setFieldValue }) => (
          <>
            <Modal
              small
              confirmAndClose={false}
              show={showModal}
              onModalClose={() => {
                formik.current?.setFieldValue('status', '')
                onClose()
              }}
              onConfirm={() => handleConfirmationUpdate(values)}
            >
              <Row style={{ marginBottom: "10px" }}>
                <Col md={4} sm={12}>
                  <CustomField>
                    <Field
                      name='status'
                      label='Status'
                      component={SelectField}
                      onChange={(e) => { setFieldValue('status', e) }}
                      options={statusOptions}
                      isRequired
                    />
                  </CustomField>
                </Col>
              </Row>
              <Row>
                <Col md={12} sm={12}>
                  <Scrollbox>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>
                            <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                              Deal
                            </GridColumnTitle>
                          </TableHeader>
                          <TableHeader>
                            <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                              Doc
                            </GridColumnTitle>
                          </TableHeader>
                          <TableHeader>
                            <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                              Current Status
                            </GridColumnTitle>
                          </TableHeader>
                          <TableHeader>
                            <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                              Future Status
                            </GridColumnTitle>
                          </TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPayments.length ? selectedPayments.map(payment => (
                          <TableRow key={payment.idInstallments}>
                            <TableCollum>
                              <InlineLabel>Deal</InlineLabel>
                              <LabelEdit>
                                {payment.deal}
                              </LabelEdit>
                            </TableCollum>
                            <TableCollum>
                              <InlineLabel>Doc</InlineLabel>
                              <LabelEdit>
                                {payment.doc}
                              </LabelEdit>
                            </TableCollum>
                            <TableCollum>
                              <InlineLabel>Current Status</InlineLabel>
                              <LabelEdit>
                                {payment.status.label}
                              </LabelEdit>
                            </TableCollum>
                            <TableCollum>
                              <InlineLabel>Future Status</InlineLabel>
                              <LabelEdit>
                                {values.status && statusOptions.find(item => item.value === values.status).label}
                              </LabelEdit>
                            </TableCollum>
                          </TableRow>
                        )) : ''}
                      </TableBody>
                    </Table>
                  </Scrollbox>
                </Col>
              </Row>
            </Modal>
            <ModalConfirmation
              text="Do you want to proceed with the status change?"
              textButtonCancel="CANCEL"
              textButtonConfirm="CONFIRM"
              showTitle={false}
              show={modalConfirmation}
              onConfirm={() => {
                if (values.status === 'paid' || values.status === 'aborted') {
                  setModalConfirmationRisk(true)
                } else {
                  handleUpdate(values.status)
                }
                setModalConfirmation(false)
              }}
              onCancel={() => {
                formik.current?.setFieldValue('status', '')
                setModalConfirmation(false)
              }}
            />
            <ModalConfirmation
              text="Changes made to paid and aborted statuses cannot be undone. Do you want to proceed?"
              textButtonCancel="CANCEL"
              textButtonConfirm="CONFIRM"
              showTitle={false}
              show={modalConfirmationRisk}
              onConfirm={() => {
                setModalConfirmationRisk(false)
                handleUpdate(values.status)
              }}
              onCancel={() => {
                formik.current?.setFieldValue('status', '')
                setModalConfirmationRisk(false)
              }}
            />
          </>
        )}
      </Formik>
      <Modal
        onlyConfirmation={true}
        show={modalReportUpdate}
        onModalClose={() => setModalReportUpdate(false)}
        onConfirm={() => {
          onUpdated && onUpdated()
        }}
      >
        <Row>
          <Col md={12} sm={12}>
            <Scrollbox>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>
                      <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                        Deal
                      </GridColumnTitle>
                    </TableHeader>
                    <TableHeader>
                      <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                        Doc
                      </GridColumnTitle>
                    </TableHeader>
                    <TableHeader>
                      <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                        Current Status
                      </GridColumnTitle>
                    </TableHeader>
                    <TableHeader>
                      <GridColumnTitle style={{ textTransform: 'uppercase' }}>
                        <Icon name="ok-green" />
                        <IconSeparator />
                        <Icon name="not-ok" />
                      </GridColumnTitle>
                    </TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {updatedPayments.length ? updatedPayments.map(payment => (
                    <TableRow key={payment.idInstallments}>
                      <TableCollum>
                        <InlineLabel>Deal</InlineLabel>
                        <LabelEdit>
                          {payment.deal}
                        </LabelEdit>
                      </TableCollum>
                      <TableCollum>
                        <InlineLabel>Doc</InlineLabel>
                        <LabelEdit>
                          {payment.doc}
                        </LabelEdit>
                      </TableCollum>
                      <TableCollum>
                        <InlineLabel>Current Status</InlineLabel>
                        <LabelEdit>
                          {payment.status}
                        </LabelEdit>
                      </TableCollum>
                      <TableCollum>
                        <LabelEdit>
                          {payment.report === 'Updated' ? <Icon name="ok-green" /> : <Icon name="not-ok" />}
                        </LabelEdit>
                      </TableCollum>
                    </TableRow>
                  )) : ''}
                </TableBody>
              </Table>
            </Scrollbox>
          </Col>
        </Row>
        <Row style={{ justifyContent: 'center' }}>
          <InfoReport>
            For more information, access your email.
          </InfoReport>
        </Row>
      </Modal>
      {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
    </>
  );
}